/**
 * CUDOLLA Learning Center interactions
 * File: assets/cudolla-blog.js
 *
 * Features:
 * - Automatic H2/H3 table of contents with stable anchor IDs
 * - Active-section highlighting and smooth in-page navigation
 * - Reading progress that supports Horizon's .page-wrapper scroll container
 * - Native share and resilient copy-link fallbacks
 * - Accessible article image lightbox
 * - Shopify Theme Editor section lifecycle support
 *
 * No dependencies. No cookies. No external requests.
 */

(function cudollaLearningCenterBootstrap() {
  'use strict';

  var VERSION = '1.0.0';
  var ARTICLE_SELECTOR = '[data-cudolla-article]';
  var LEARNING_CENTER_SELECTOR = '.cud-lc';
  var DESKTOP_MEDIA_QUERY = '(min-width: 990px)';

  if (
    window.CUDOLLABlog &&
    typeof window.CUDOLLABlog.init === 'function' &&
    window.CUDOLLABlog.version
  ) {
    window.CUDOLLABlog.init(document);
    return;
  }

  var controllers = new Set();
  var controllerByRoot = new WeakMap();
  var reducedMotionQuery = window.matchMedia
    ? window.matchMedia('(prefers-reduced-motion: reduce)')
    : null;

  function prefersReducedMotion() {
    return Boolean(reducedMotionQuery && reducedMotionQuery.matches);
  }

  function clamp(value, minimum, maximum) {
    return Math.min(Math.max(value, minimum), maximum);
  }

  function parseBoolean(value, fallback) {
    if (value === undefined || value === null || value === '') {
      return Boolean(fallback);
    }

    return String(value).toLowerCase() === 'true';
  }

  function parseInteger(value, fallback) {
    var parsed = Number.parseInt(value, 10);
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  function isElement(value) {
    return value instanceof Element;
  }

  function getPageWrapper() {
    return document.querySelector('.page-wrapper');
  }

  function elementCanScroll(element) {
    if (!element || !isElement(element)) {
      return false;
    }

    var style = window.getComputedStyle(element);
    var overflowY = style.overflowY || '';
    var permitsScrolling = /(auto|scroll|overlay)/i.test(overflowY);

    return permitsScrolling && element.scrollHeight > element.clientHeight + 2;
  }

  function getScrollRoot() {
    var pageWrapper = getPageWrapper();

    if (elementCanScroll(pageWrapper)) {
      return pageWrapper;
    }

    return window;
  }

  function getScrollMetrics(scrollRoot) {
    if (scrollRoot === window) {
      var scrollingElement = document.scrollingElement || document.documentElement;
      return {
        top: scrollingElement.scrollTop || window.scrollY || 0,
        viewportHeight: window.innerHeight || document.documentElement.clientHeight || 0,
        scrollHeight: scrollingElement.scrollHeight || document.documentElement.scrollHeight || 0,
        viewportTop: 0
      };
    }

    var rootRect = scrollRoot.getBoundingClientRect();

    return {
      top: scrollRoot.scrollTop,
      viewportHeight: scrollRoot.clientHeight,
      scrollHeight: scrollRoot.scrollHeight,
      viewportTop: rootRect.top
    };
  }

  function getElementTop(element, scrollRoot) {
    var elementRect = element.getBoundingClientRect();
    var metrics = getScrollMetrics(scrollRoot);

    if (scrollRoot === window) {
      return elementRect.top + metrics.top;
    }

    return elementRect.top - metrics.viewportTop + metrics.top;
  }

  function readCssPixelVariable(element, variableName) {
    if (!element) {
      return 0;
    }

    var value = window.getComputedStyle(element).getPropertyValue(variableName);
    var parsed = Number.parseFloat(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  function getAnchorOffset() {
    var bodyValue = readCssPixelVariable(document.body, '--header-height');
    var documentValue = readCssPixelVariable(document.documentElement, '--header-height');
    var groupValue = readCssPixelVariable(document.body, '--header-group-height');
    var headerHeight = Math.max(bodyValue, documentValue, groupValue, 0);

    return clamp(headerHeight + 24, 36, 190);
  }

  function safeDecodeHash(hash) {
    if (!hash || hash === '#') {
      return '';
    }

    try {
      return decodeURIComponent(hash.replace(/^#/, ''));
    } catch (error) {
      return hash.replace(/^#/, '');
    }
  }

  function updateAddressHash(id, replace) {
    if (!id || !window.history || typeof window.history.pushState !== 'function') {
      return;
    }

    var nextHash = '#' + encodeURIComponent(id);

    if (window.location.hash === nextHash) {
      return;
    }

    try {
      if (replace) {
        window.history.replaceState(null, '', nextHash);
      } else {
        window.history.pushState(null, '', nextHash);
      }
    } catch (error) {
      // Some embedded previews can restrict history operations.
    }
  }

  function scrollToElement(element, options) {
    if (!element) {
      return;
    }

    var settings = options || {};
    var scrollRoot = getScrollRoot();
    var metrics = getScrollMetrics(scrollRoot);
    var destination = getElementTop(element, scrollRoot) - getAnchorOffset();
    var maximum = Math.max(0, metrics.scrollHeight - metrics.viewportHeight);
    var top = clamp(destination, 0, maximum);
    var behavior = prefersReducedMotion() ? 'auto' : 'smooth';

    if (scrollRoot === window) {
      window.scrollTo({ top: top, behavior: behavior });
    } else if (typeof scrollRoot.scrollTo === 'function') {
      scrollRoot.scrollTo({ top: top, behavior: behavior });
    } else {
      scrollRoot.scrollTop = top;
    }

    if (settings.updateHash !== false && element.id) {
      updateAddressHash(element.id, Boolean(settings.replaceHash));
    }
  }

  function slugifyHeading(value) {
    var normalized = String(value || '').trim();

    if (typeof normalized.normalize === 'function') {
      normalized = normalized.normalize('NFKD').replace(/[\u0300-\u036f]/g, '');
    }

    normalized = normalized
      .replace(/[\u2018\u2019']/g, '')
      .replace(/&/g, ' and ')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .replace(/-{2,}/g, '-');

    return normalized || 'article-section';
  }

  function uniqueHeadingId(base, reservedIds) {
    var candidate = base;
    var suffix = 2;

    while (reservedIds.has(candidate)) {
      candidate = base + '-' + suffix;
      suffix += 1;
    }

    reservedIds.add(candidate);
    return candidate;
  }

  function isEligibleHeading(heading) {
    if (!heading || !heading.textContent || !heading.textContent.trim()) {
      return false;
    }

    if (
      heading.matches('[hidden], [aria-hidden="true"], [data-cud-toc-exclude], .cud-toc-exclude') ||
      heading.closest('[hidden], [aria-hidden="true"], [data-cud-toc-exclude], .cud-toc-exclude')
    ) {
      return false;
    }

    return true;
  }

  function largestImageSource(image) {
    var explicitSource = image.getAttribute('data-cud-lightbox-src');
    if (explicitSource) {
      return explicitSource;
    }

    var srcset = image.getAttribute('srcset');
    if (srcset) {
      var candidates = srcset
        .split(',')
        .map(function mapCandidate(candidate) {
          var parts = candidate.trim().split(/\s+/);
          var descriptor = parts[parts.length - 1] || '';
          var score = 1;

          if (/\d+w$/.test(descriptor)) {
            score = Number.parseInt(descriptor, 10) || 1;
          } else if (/\d+(\.\d+)?x$/.test(descriptor)) {
            score = (Number.parseFloat(descriptor) || 1) * 10000;
          }

          return {
            url: parts[0],
            score: score
          };
        })
        .filter(function filterCandidate(candidate) {
          return Boolean(candidate.url);
        })
        .sort(function sortCandidate(first, second) {
          return second.score - first.score;
        });

      if (candidates.length) {
        return candidates[0].url;
      }
    }

    return image.currentSrc || image.getAttribute('src') || '';
  }

  function fallbackCopyText(value) {
    return new Promise(function copyPromise(resolve, reject) {
      var textarea = document.createElement('textarea');
      textarea.value = value;
      textarea.setAttribute('readonly', '');
      textarea.setAttribute('aria-hidden', 'true');
      textarea.style.position = 'fixed';
      textarea.style.inset = '-9999px auto auto -9999px';
      textarea.style.opacity = '0';
      textarea.style.pointerEvents = 'none';

      document.body.appendChild(textarea);
      textarea.focus({ preventScroll: true });
      textarea.select();
      textarea.setSelectionRange(0, textarea.value.length);

      try {
        var successful = document.execCommand('copy');
        document.body.removeChild(textarea);

        if (successful) {
          resolve();
        } else {
          reject(new Error('Copy command was not accepted.'));
        }
      } catch (error) {
        document.body.removeChild(textarea);
        reject(error);
      }
    });
  }

  function copyText(value) {
    if (
      navigator.clipboard &&
      typeof navigator.clipboard.writeText === 'function' &&
      window.isSecureContext
    ) {
      return navigator.clipboard.writeText(value).catch(function clipboardFallback() {
        return fallbackCopyText(value);
      });
    }

    return fallbackCopyText(value);
  }

  function dispatchReadyEvent(root, detail) {
    try {
      root.dispatchEvent(
        new CustomEvent('cudolla:article:ready', {
          bubbles: true,
          detail: detail || {}
        })
      );
    } catch (error) {
      // CustomEvent is supported by target browsers; failure is non-critical.
    }
  }

  function ListenerBag() {
    this.cleanups = [];
  }

  ListenerBag.prototype.add = function addListener(target, type, handler, options) {
    if (!target || typeof target.addEventListener !== 'function') {
      return;
    }

    target.addEventListener(type, handler, options);
    this.cleanups.push(function removeListener() {
      target.removeEventListener(type, handler, options);
    });
  };

  ListenerBag.prototype.addMedia = function addMediaListener(mediaQuery, handler) {
    if (!mediaQuery) {
      return;
    }

    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', handler);
      this.cleanups.push(function removeModernMediaListener() {
        mediaQuery.removeEventListener('change', handler);
      });
    } else if (typeof mediaQuery.addListener === 'function') {
      mediaQuery.addListener(handler);
      this.cleanups.push(function removeLegacyMediaListener() {
        mediaQuery.removeListener(handler);
      });
    }
  };

  ListenerBag.prototype.clear = function clearListeners() {
    this.cleanups.splice(0).forEach(function runCleanup(cleanup) {
      try {
        cleanup();
      } catch (error) {
        // Cleanup should never prevent the remaining cleanup functions.
      }
    });
  };

  function CudollaArticleController(root) {
    this.root = root;
    this.listeners = new ListenerBag();
    this.content = root.querySelector('[data-cud-article-content]');
    this.main = root.querySelector('[data-cud-article-main]') || this.content;
    this.pageWrapper = getPageWrapper();
    this.desktopQuery = window.matchMedia ? window.matchMedia(DESKTOP_MEDIA_QUERY) : null;
    this.tocStates = [];
    this.tocMediaBound = false;
    this.activeHeadingId = '';
    this.frame = 0;
    this.resizeObserver = null;
    this.mutationObserver = null;
    this.statusTimers = new Map();
    this.labelTimers = new Map();
    this.lightbox = null;
    this.lightboxImage = null;
    this.lightboxCaption = null;
    this.lightboxPreviousFocus = null;
    this.scrollLockState = null;
    this.isLightboxOpen = false;
    this.destroyed = false;
  }

  CudollaArticleController.prototype.init = function initArticle() {
    if (this.root.dataset.cudJsReady === 'true') {
      return;
    }

    this.root.dataset.cudJsReady = 'true';
    document.documentElement.classList.add('cudolla-learning-center-js');

    this.buildTablesOfContents();
    this.prepareLightbox();
    this.bindArticleInteractions();
    this.bindScrollTracking();
    this.observeLayoutChanges();
    this.scheduleUpdate();
    this.restoreHashPosition();

    dispatchReadyEvent(this.root, {
      version: VERSION,
      headings: this.getTrackedHeadings().length
    });
  };

  CudollaArticleController.prototype.listen = function listen(target, type, handler, options) {
    this.listeners.add(target, type, handler, options);
  };

  CudollaArticleController.prototype.setTimer = function setControllerTimer(callback, delay) {
    var controller = this;
    var timer = window.setTimeout(function timerCallback() {
      controller.statusTimers.delete(timer);
      callback();
    }, delay);

    this.statusTimers.set(timer, true);
    return timer;
  };

  CudollaArticleController.prototype.clearTimers = function clearControllerTimers() {
    this.statusTimers.forEach(function clearTimer(value, timer) {
      window.clearTimeout(timer);
    });
    this.statusTimers.clear();

    this.labelTimers.forEach(function clearLabelTimer(timer) {
      window.clearTimeout(timer);
    });
    this.labelTimers.clear();
  };

  CudollaArticleController.prototype.getTrackedHeadings = function getTrackedHeadings() {
    if (!this.tocStates.length) {
      return [];
    }

    return this.tocStates[0].headings || [];
  };

  CudollaArticleController.prototype.buildTablesOfContents = function buildTablesOfContents() {
    var controller = this;
    this.activeHeadingId = '';
    var tocElements = Array.from(this.root.querySelectorAll('[data-cud-toc]'));

    this.tocStates = [];

    tocElements.forEach(function buildToc(toc) {
      var contentSelector = toc.dataset.cudTocContentSelector || '[data-cud-article-content]';
      var content = controller.root.querySelector(contentSelector) || document.querySelector(contentSelector);
      var selector = toc.dataset.cudTocSelector || 'h2,h3';
      var minimumItems = Math.max(1, parseInteger(toc.dataset.cudTocMinItems, 2));
      var list = toc.querySelector('[data-cud-toc-list]');
      var navigation = toc.querySelector('[data-cud-toc-nav]');
      var status = toc.querySelector('[data-cud-toc-status]');

      if (!content || !list || !navigation) {
        toc.hidden = true;
        toc.dataset.cudTocReady = 'true';
        return;
      }

      var headings = Array.from(content.querySelectorAll(selector)).filter(isEligibleHeading);

      if (headings.length < minimumItems) {
        list.replaceChildren();
        navigation.hidden = true;
        navigation.setAttribute('aria-busy', 'false');
        toc.hidden = true;
        toc.open = false;
        toc.dataset.cudTocReady = 'true';

        if (status) {
          status.textContent = 'Article navigation is unavailable for this guide.';
        }
        return;
      }

      var headingSet = new Set(headings);
      var reservedIds = new Set();

      document.querySelectorAll('[id]').forEach(function reserveExistingId(element) {
        if (!headingSet.has(element) && element.id) {
          reservedIds.add(element.id);
        }
      });

      var fragment = document.createDocumentFragment();
      var linksById = new Map();

      headings.forEach(function createTocLink(heading, index) {
        var text = heading.textContent.replace(/\s+/g, ' ').trim();
        var baseId = heading.id ? heading.id.trim() : slugifyHeading(text);
        var resolvedId = uniqueHeadingId(baseId || 'article-section-' + (index + 1), reservedIds);
        var level = heading.tagName.toLowerCase() === 'h3' ? 3 : 2;

        heading.id = resolvedId;
        heading.dataset.cudTocHeading = 'true';

        var item = document.createElement('li');
        item.className = level === 3
          ? 'cud-toc__item cud-toc__item--subsection'
          : 'cud-toc__item cud-toc__item--section';
        item.dataset.level = String(level);

        var link = document.createElement('a');
        link.href = '#' + encodeURIComponent(resolvedId);
        link.textContent = text;
        link.dataset.cudTocLink = '';
        link.dataset.cudTocTarget = resolvedId;

        controller.listen(link, 'click', function onTocLinkClick(event) {
          event.preventDefault();
          controller.setActiveHeading(resolvedId);
          scrollToElement(heading, { updateHash: true });

          if (controller.desktopQuery && !controller.desktopQuery.matches) {
            toc.open = false;
          }

          if (event.detail === 0) {
            controller.setTimer(function focusHeadingAfterScroll() {
              if (!heading.isConnected) {
                return;
              }

              heading.setAttribute('tabindex', '-1');
              heading.focus({ preventScroll: true });
            }, prefersReducedMotion() ? 0 : 420);
          }
        });

        item.appendChild(link);
        fragment.appendChild(item);
        linksById.set(resolvedId, link);
      });

      list.replaceChildren(fragment);
      navigation.hidden = false;
      navigation.setAttribute('aria-busy', 'false');
      toc.hidden = false;
      toc.dataset.cudTocReady = 'true';

      if (status) {
        status.textContent = headings.length + ' article sections available.';
      }

      delete toc.dataset.cudTocViewport;
      controller.syncTocDisclosure(toc);

      controller.tocStates.push({
        toc: toc,
        navigation: navigation,
        list: list,
        headings: headings,
        linksById: linksById
      });
    });

    if (this.desktopQuery && !this.tocMediaBound) {
      this.tocMediaBound = true;
      this.listeners.addMedia(this.desktopQuery, function onDesktopChange() {
        controller.tocStates.forEach(function syncState(state) {
          controller.syncTocDisclosure(state.toc);
        });
        controller.scheduleUpdate();
      });
    }
  };

  CudollaArticleController.prototype.syncTocDisclosure = function syncTocDisclosure(toc) {
    if (!toc || toc.hidden) {
      return;
    }

    var desktop = this.desktopQuery ? this.desktopQuery.matches : window.innerWidth >= 990;
    var openDesktop = parseBoolean(toc.dataset.cudTocOpenDesktop, true);
    var nextViewport = desktop ? 'desktop' : 'mobile';

    if (toc.dataset.cudTocViewport === nextViewport) {
      return;
    }

    toc.dataset.cudTocViewport = nextViewport;

    if (desktop && openDesktop) {
      toc.open = true;
    } else {
      toc.open = false;
    }
  };

  CudollaArticleController.prototype.setActiveHeading = function setActiveHeading(id) {
    if (!id || this.activeHeadingId === id) {
      return;
    }

    this.activeHeadingId = id;
    this.root.dataset.cudActiveHeading = id;

    this.tocStates.forEach(function updateTocState(state) {
      state.linksById.forEach(function updateLink(link, linkId) {
        var active = linkId === id;
        link.classList.toggle('is-active', active);

        if (active) {
          link.setAttribute('aria-current', 'location');
        } else {
          link.removeAttribute('aria-current');
        }
      });
    });
  };

  CudollaArticleController.prototype.updateActiveHeading = function updateActiveHeading() {
    var headings = this.getTrackedHeadings();

    if (!headings.length) {
      return;
    }

    var scrollRoot = getScrollRoot();
    var metrics = getScrollMetrics(scrollRoot);
    var threshold = metrics.viewportTop + getAnchorOffset() + 8;
    var active = headings[0];

    headings.forEach(function findActiveHeading(heading) {
      if (heading.getBoundingClientRect().top <= threshold) {
        active = heading;
      }
    });

    var atBottom = metrics.top + metrics.viewportHeight >= metrics.scrollHeight - 4;
    if (atBottom) {
      active = headings[headings.length - 1];
    }

    this.setActiveHeading(active.id);
  };

  CudollaArticleController.prototype.updateReadingProgress = function updateReadingProgress() {
    var progress = this.root.querySelector('[data-cud-reading-progress]');
    var bar = progress ? progress.querySelector('[data-cud-reading-progress-bar]') : null;
    var target = this.main || this.content;

    if (!progress || !bar || !target) {
      return;
    }

    var scrollRoot = getScrollRoot();
    var metrics = getScrollMetrics(scrollRoot);
    var targetTop = getElementTop(target, scrollRoot);
    var targetHeight = Math.max(target.getBoundingClientRect().height, target.scrollHeight || 0);
    var start = targetTop - getAnchorOffset();
    var endViewportAllowance = Math.min(metrics.viewportHeight * 0.82, 720);
    var end = targetTop + targetHeight - endViewportAllowance;

    if (end <= start) {
      end = start + Math.max(targetHeight, 1);
    }

    var ratio = clamp((metrics.top - start) / (end - start), 0, 1);

    if (metrics.top + metrics.viewportHeight >= metrics.scrollHeight - 4) {
      ratio = 1;
    }

    var exactPercentage = ratio * 100;
    var roundedPercentage = Math.round(exactPercentage);

    bar.style.width = exactPercentage.toFixed(2) + '%';
    progress.style.setProperty('--cud-reading-progress', ratio.toFixed(4));
    progress.setAttribute('aria-valuenow', String(roundedPercentage));
    progress.setAttribute('aria-valuetext', roundedPercentage + '% read');
  };

  CudollaArticleController.prototype.scheduleUpdate = function scheduleUpdate() {
    var controller = this;

    if (this.frame || this.destroyed) {
      return;
    }

    this.frame = window.requestAnimationFrame(function runScheduledUpdate() {
      controller.frame = 0;

      if (controller.destroyed || !controller.root.isConnected) {
        return;
      }

      controller.updateReadingProgress();
      controller.updateActiveHeading();
    });
  };

  CudollaArticleController.prototype.bindScrollTracking = function bindScrollTracking() {
    var controller = this;
    var schedule = function scheduleFromEvent() {
      controller.scheduleUpdate();
    };

    this.listen(window, 'scroll', schedule, { passive: true });
    this.listen(window, 'resize', schedule, { passive: true });
    this.listen(window, 'orientationchange', schedule, { passive: true });

    if (this.pageWrapper) {
      this.listen(this.pageWrapper, 'scroll', schedule, { passive: true });
    }

    this.listen(window, 'hashchange', function onHashChange() {
      var id = safeDecodeHash(window.location.hash);
      var target = id ? document.getElementById(id) : null;

      if (target && controller.root.contains(target)) {
        controller.setActiveHeading(id);
      }
    });
  };

  CudollaArticleController.prototype.observeLayoutChanges = function observeLayoutChanges() {
    var controller = this;

    if (typeof window.ResizeObserver === 'function') {
      this.resizeObserver = new ResizeObserver(function onResizeObserved() {
        controller.scheduleUpdate();
      });

      this.resizeObserver.observe(this.root);
      if (this.main) {
        this.resizeObserver.observe(this.main);
      }
      if (this.content && this.content !== this.main) {
        this.resizeObserver.observe(this.content);
      }
    }

    this.root.querySelectorAll('img').forEach(function listenForImage(image) {
      if (!image.complete) {
        controller.listen(image, 'load', function onImageLoad() {
          controller.scheduleUpdate();
          controller.prepareLightboxImage(image);
        }, { once: true });
      }
    });

    if (
      window.Shopify &&
      window.Shopify.designMode &&
      typeof window.MutationObserver === 'function' &&
      this.content
    ) {
      var rebuildTimer = 0;

      this.mutationObserver = new MutationObserver(function onContentMutation() {
        window.clearTimeout(rebuildTimer);
        rebuildTimer = window.setTimeout(function rebuildAfterMutation() {
          if (!controller.destroyed) {
            controller.buildTablesOfContents();
            controller.prepareLightboxImages();
            controller.scheduleUpdate();
          }
        }, 180);
      });

      this.mutationObserver.observe(this.content, {
        childList: true,
        subtree: true,
        characterData: true
      });
    }
  };

  CudollaArticleController.prototype.restoreHashPosition = function restoreHashPosition() {
    var controller = this;
    var id = safeDecodeHash(window.location.hash);

    if (!id) {
      return;
    }

    this.setTimer(function restoreInitialHash() {
      var target = document.getElementById(id);

      if (target && controller.root.contains(target)) {
        controller.setActiveHeading(id);
        scrollToElement(target, { updateHash: false });
      }
    }, 80);
  };

  CudollaArticleController.prototype.announceShareStatus = function announceShareStatus(panel, message) {
    if (!panel) {
      return;
    }

    var status = panel.querySelector('[data-cud-share-status]');
    if (!status) {
      return;
    }

    status.textContent = '';

    window.requestAnimationFrame(function announceOnNextFrame() {
      status.textContent = message || '';
    });
  };

  CudollaArticleController.prototype.temporarilyChangeCopyLabel = function temporarilyChangeCopyLabel(button, label) {
    var labelElement = button.querySelector('[data-cud-copy-text]');
    if (!labelElement) {
      return;
    }

    var original = button.dataset.copyLabel || labelElement.textContent || 'Copy link';
    var existingTimer = this.labelTimers.get(button);

    if (existingTimer) {
      window.clearTimeout(existingTimer);
    }

    labelElement.textContent = label;
    button.dataset.cudCopyState = label === original ? 'idle' : 'complete';

    var controller = this;
    var timer = window.setTimeout(function resetCopyLabel() {
      labelElement.textContent = original;
      button.dataset.cudCopyState = 'idle';
      controller.labelTimers.delete(button);
    }, 2200);

    this.labelTimers.set(button, timer);
  };

  CudollaArticleController.prototype.handleCopyButton = function handleCopyButton(button) {
    var controller = this;
    var panel = button.closest('[data-cud-share]');
    var url = button.dataset.copyUrl || (panel ? panel.dataset.cudShareUrl : '') || window.location.href;
    var copiedLabel = button.dataset.copiedLabel || 'Copied';
    var errorLabel = button.dataset.errorLabel || 'Copy unavailable';

    if (!url || button.disabled) {
      return;
    }

    button.disabled = true;

    copyText(url)
      .then(function onCopySuccess() {
        controller.temporarilyChangeCopyLabel(button, copiedLabel);
        controller.announceShareStatus(panel, copiedLabel + '.');
      })
      .catch(function onCopyFailure() {
        controller.temporarilyChangeCopyLabel(button, errorLabel);
        controller.announceShareStatus(panel, errorLabel + '.');
      })
      .finally(function restoreCopyButton() {
        button.disabled = false;
      });
  };

  CudollaArticleController.prototype.handleNativeShare = function handleNativeShare(button) {
    var controller = this;
    var panel = button.closest('[data-cud-share]');
    var url = button.dataset.shareUrl || (panel ? panel.dataset.cudShareUrl : '') || window.location.href;
    var title = button.dataset.shareTitle || (panel ? panel.dataset.cudShareTitle : '') || document.title;
    var text = button.dataset.shareText || (panel ? panel.dataset.cudShareText : '') || '';
    var successLabel = button.dataset.successLabel || 'Share options opened';
    var fallbackLabel = button.dataset.fallbackLabel || 'Link copied';
    var errorLabel = button.dataset.errorLabel || 'Sharing is unavailable';
    var payload = { title: title, text: text, url: url };

    if (button.disabled) {
      return;
    }

    button.disabled = true;

    var canUseNativeShare = typeof navigator.share === 'function';
    if (canUseNativeShare && typeof navigator.canShare === 'function') {
      try {
        canUseNativeShare = navigator.canShare(payload);
      } catch (error) {
        canUseNativeShare = false;
      }
    }

    if (canUseNativeShare) {
      navigator.share(payload)
        .then(function onShareSuccess() {
          controller.announceShareStatus(panel, successLabel + '.');
        })
        .catch(function onShareFailure(error) {
          if (error && error.name === 'AbortError') {
            return;
          }

          return copyText(url)
            .then(function onShareFallbackCopy() {
              controller.announceShareStatus(panel, fallbackLabel + '.');
            })
            .catch(function onShareFallbackFailure() {
              controller.announceShareStatus(panel, errorLabel + '.');
            });
        })
        .finally(function restoreNativeShareButton() {
          button.disabled = false;
        });

      return;
    }

    copyText(url)
      .then(function onNativeFallbackCopy() {
        controller.announceShareStatus(panel, fallbackLabel + '.');
      })
      .catch(function onNativeFallbackFailure() {
        controller.announceShareStatus(panel, errorLabel + '.');
      })
      .finally(function restoreFallbackButton() {
        button.disabled = false;
      });
  };

  CudollaArticleController.prototype.prepareLightbox = function prepareLightbox() {
    this.lightbox = this.root.querySelector('[data-cud-lightbox]');

    if (!this.lightbox) {
      return;
    }

    this.lightboxImage = this.lightbox.querySelector('[data-cud-lightbox-image]');
    this.lightboxCaption = this.lightbox.querySelector('[data-cud-lightbox-caption]');
    this.lightbox.setAttribute('aria-hidden', 'true');

    var backdrop = this.lightbox.querySelector('.cud-lightbox__backdrop');
    if (backdrop) {
      backdrop.setAttribute('tabindex', '-1');
    }

    this.prepareLightboxImages();
  };

  CudollaArticleController.prototype.lightboxEnabled = function lightboxEnabled() {
    var rootEnabled = parseBoolean(this.root.dataset.enableLightbox, true);
    var contentEnabled = this.content
      ? parseBoolean(this.content.dataset.enableLightbox, rootEnabled)
      : rootEnabled;

    return rootEnabled && contentEnabled;
  };

  CudollaArticleController.prototype.prepareLightboxImages = function prepareLightboxImages() {
    var controller = this;

    if (!this.lightbox || !this.lightboxEnabled()) {
      return;
    }

    var images = [];
    var featuredImage = this.root.querySelector('[data-cud-featured-image]');

    if (featuredImage) {
      images.push(featuredImage);
    }

    if (this.content) {
      images = images.concat(Array.from(this.content.querySelectorAll('img')));
    }

    images.forEach(function prepareImage(image) {
      controller.prepareLightboxImage(image);
    });
  };

  CudollaArticleController.prototype.prepareLightboxImage = function prepareLightboxImage(image) {
    if (!image || image.dataset.cudLightboxPrepared === 'true' || !this.lightboxEnabled()) {
      return;
    }

    if (
      image.matches('[aria-hidden="true"], [data-cud-no-lightbox], .cud-no-lightbox') ||
      image.closest(
        '[data-cud-no-lightbox], .cud-no-lightbox, .cud-product-card, .cud-related-card, .cud-article__author-avatar, .cud-share, .cud-toc'
      )
    ) {
      return;
    }

    if (image.closest('a') && !image.hasAttribute('data-cud-lightbox-force')) {
      return;
    }

    var finalize = function finalizePreparedImage() {
      if (
        image.dataset.cudLightboxPrepared === 'true' ||
        (image.complete && image.naturalWidth === 0) ||
        (!image.hasAttribute('data-cud-featured-image') && image.naturalWidth > 0 && image.naturalWidth < 240)
      ) {
        return;
      }

      image.dataset.cudLightboxPrepared = 'true';
      image.setAttribute('data-cud-lightbox-trigger', '');
      image.setAttribute('tabindex', '0');
      image.setAttribute('role', 'button');
      image.setAttribute('aria-haspopup', 'dialog');

      var alt = image.getAttribute('alt') || 'article image';
      image.setAttribute('aria-label', 'View ' + alt + ' in full screen');
    };

    if (image.complete) {
      finalize();
    } else {
      this.listen(image, 'load', finalize, { once: true });
    }
  };

  CudollaArticleController.prototype.imageCaption = function imageCaption(image) {
    var explicit = image.getAttribute('data-cud-lightbox-caption');
    if (explicit) {
      return explicit.trim();
    }

    var figure = image.closest('figure');
    if (figure) {
      var figcaption = figure.querySelector('figcaption');
      if (figcaption && figcaption.textContent.trim()) {
        return figcaption.textContent.replace(/\s+/g, ' ').trim();
      }
    }

    return '';
  };

  CudollaArticleController.prototype.lockPageScroll = function lockPageScroll() {
    if (this.scrollLockState) {
      return;
    }

    var scrollRoot = getScrollRoot();
    var html = document.documentElement;
    var body = document.body;

    this.scrollLockState = {
      root: scrollRoot,
      htmlOverflow: html.style.overflow,
      htmlOverscroll: html.style.overscrollBehavior,
      bodyOverflow: body.style.overflow,
      bodyOverscroll: body.style.overscrollBehavior,
      rootOverflow: scrollRoot !== window ? scrollRoot.style.overflow : '',
      rootOverscroll: scrollRoot !== window ? scrollRoot.style.overscrollBehavior : ''
    };

    html.classList.add('cudolla-lightbox-open');
    body.classList.add('cudolla-lightbox-open');
    html.style.overflow = 'hidden';
    html.style.overscrollBehavior = 'none';
    body.style.overflow = 'hidden';
    body.style.overscrollBehavior = 'none';

    if (scrollRoot !== window) {
      scrollRoot.style.overflow = 'hidden';
      scrollRoot.style.overscrollBehavior = 'none';
    }
  };

  CudollaArticleController.prototype.unlockPageScroll = function unlockPageScroll() {
    if (!this.scrollLockState) {
      return;
    }

    var state = this.scrollLockState;
    var html = document.documentElement;
    var body = document.body;

    html.classList.remove('cudolla-lightbox-open');
    body.classList.remove('cudolla-lightbox-open');
    html.style.overflow = state.htmlOverflow;
    html.style.overscrollBehavior = state.htmlOverscroll;
    body.style.overflow = state.bodyOverflow;
    body.style.overscrollBehavior = state.bodyOverscroll;

    if (state.root !== window && state.root) {
      state.root.style.overflow = state.rootOverflow;
      state.root.style.overscrollBehavior = state.rootOverscroll;
    }

    this.scrollLockState = null;
  };

  CudollaArticleController.prototype.openLightbox = function openLightbox(image) {
    if (
      !this.lightbox ||
      !this.lightboxImage ||
      !image ||
      !this.lightboxEnabled() ||
      this.isLightboxOpen
    ) {
      return;
    }

    var source = largestImageSource(image);
    if (!source) {
      return;
    }

    this.lightboxPreviousFocus = document.activeElement;
    this.lightboxImage.src = source;
    this.lightboxImage.alt = image.getAttribute('alt') || '';

    var caption = this.imageCaption(image);
    if (this.lightboxCaption) {
      this.lightboxCaption.textContent = caption;
      this.lightboxCaption.hidden = !caption;
    }

    this.isLightboxOpen = true;
    this.lightbox.hidden = false;
    this.lightbox.setAttribute('aria-hidden', 'false');
    this.lightbox.dataset.cudLightboxOpen = 'true';
    this.root.dataset.cudLightboxOpen = 'true';
    this.lockPageScroll();

    var closeButton = this.lightbox.querySelector('.cud-lightbox__close');
    if (closeButton) {
      window.requestAnimationFrame(function focusCloseButton() {
        closeButton.focus({ preventScroll: true });
      });
    }
  };

  CudollaArticleController.prototype.closeLightbox = function closeLightbox(options) {
    if (!this.lightbox || !this.isLightboxOpen) {
      return;
    }

    var settings = options || {};
    this.isLightboxOpen = false;
    this.lightbox.hidden = true;
    this.lightbox.setAttribute('aria-hidden', 'true');
    this.lightbox.dataset.cudLightboxOpen = 'false';
    delete this.root.dataset.cudLightboxOpen;
    this.unlockPageScroll();

    if (this.lightboxImage) {
      this.lightboxImage.removeAttribute('src');
      this.lightboxImage.alt = '';
    }

    if (this.lightboxCaption) {
      this.lightboxCaption.textContent = '';
      this.lightboxCaption.hidden = true;
    }

    if (
      settings.restoreFocus !== false &&
      this.lightboxPreviousFocus &&
      this.lightboxPreviousFocus.isConnected &&
      typeof this.lightboxPreviousFocus.focus === 'function'
    ) {
      this.lightboxPreviousFocus.focus({ preventScroll: true });
    }

    this.lightboxPreviousFocus = null;
  };

  CudollaArticleController.prototype.trapLightboxFocus = function trapLightboxFocus(event) {
    if (!this.isLightboxOpen || event.key !== 'Tab' || !this.lightbox) {
      return;
    }

    var focusable = Array.from(
      this.lightbox.querySelectorAll(
        'button:not([disabled]):not([hidden]), a[href]:not([hidden]), [tabindex]:not([tabindex="-1"]):not([hidden])'
      )
    ).filter(function visibleFocusable(element) {
      return element.getClientRects().length > 0;
    });

    if (!focusable.length) {
      event.preventDefault();
      return;
    }

    var first = focusable[0];
    var last = focusable[focusable.length - 1];
    var active = document.activeElement;

    if (event.shiftKey && active === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  };

  CudollaArticleController.prototype.handleRootClick = function handleRootClick(event) {
    var eventTarget = isElement(event.target) ? event.target : event.target.parentElement;
    if (!eventTarget) {
      return;
    }

    var nativeShareButton = eventTarget.closest('[data-cud-native-share]');
    if (nativeShareButton && this.root.contains(nativeShareButton)) {
      event.preventDefault();
      this.handleNativeShare(nativeShareButton);
      return;
    }

    var copyButton = eventTarget.closest('[data-cud-copy-link]');
    if (copyButton && this.root.contains(copyButton)) {
      event.preventDefault();
      this.handleCopyButton(copyButton);
      return;
    }

    var closeButton = eventTarget.closest('[data-cud-lightbox-close]');
    if (closeButton && this.lightbox && this.lightbox.contains(closeButton)) {
      event.preventDefault();
      this.closeLightbox();
      return;
    }

    var image = eventTarget.closest('[data-cud-lightbox-trigger]');
    if (image && this.root.contains(image)) {
      event.preventDefault();
      this.openLightbox(image);
      return;
    }

    var anchor = eventTarget.closest('a[href*="#"]');
    if (!anchor || !this.root.contains(anchor)) {
      return;
    }

    var url;
    try {
      url = new URL(anchor.href, window.location.href);
    } catch (error) {
      return;
    }

    if (
      url.origin !== window.location.origin ||
      url.pathname !== window.location.pathname ||
      !url.hash
    ) {
      return;
    }

    var id = safeDecodeHash(url.hash);
    var target = id ? document.getElementById(id) : null;

    if (target && this.root.contains(target)) {
      event.preventDefault();
      scrollToElement(target, { updateHash: true });
    }
  };

  CudollaArticleController.prototype.handleRootKeydown = function handleRootKeydown(event) {
    var eventTarget = isElement(event.target) ? event.target : event.target.parentElement;
    var image = eventTarget ? eventTarget.closest('[data-cud-lightbox-trigger]') : null;

    if (!image || !this.root.contains(image)) {
      return;
    }

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.openLightbox(image);
    }
  };

  CudollaArticleController.prototype.handleDocumentKeydown = function handleDocumentKeydown(event) {
    if (!this.isLightboxOpen) {
      return;
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      this.closeLightbox();
      return;
    }

    this.trapLightboxFocus(event);
  };

  CudollaArticleController.prototype.bindArticleInteractions = function bindArticleInteractions() {
    var controller = this;

    this.listen(this.root, 'click', function onRootClick(event) {
      controller.handleRootClick(event);
    });

    this.listen(this.root, 'keydown', function onRootKeydown(event) {
      controller.handleRootKeydown(event);
    });

    this.listen(document, 'keydown', function onDocumentKeydown(event) {
      controller.handleDocumentKeydown(event);
    });
  };

  CudollaArticleController.prototype.destroy = function destroyArticle() {
    if (this.destroyed) {
      return;
    }

    this.destroyed = true;

    if (this.frame) {
      window.cancelAnimationFrame(this.frame);
      this.frame = 0;
    }

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }

    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
      this.mutationObserver = null;
    }

    this.closeLightbox({ restoreFocus: false });
    this.unlockPageScroll();
    this.clearTimers();
    this.listeners.clear();
    this.tocStates = [];
    this.root.removeAttribute('data-cud-js-ready');
    this.root.removeAttribute('data-cud-active-heading');
  };

  function initArticleRoot(root) {
    if (!root || controllerByRoot.has(root)) {
      return;
    }

    var controller = new CudollaArticleController(root);
    controllerByRoot.set(root, controller);
    controllers.add(controller);
    controller.init();
  }

  function initLearningCenter(root) {
    if (!root || root.dataset.cudJsReady === 'true') {
      return;
    }

    root.dataset.cudJsReady = 'true';

    var activeFilter = root.querySelector('.cud-lc__filters .is-active');
    if (activeFilter) {
      var filterRail = activeFilter.closest('.cud-lc__filters');
      if (filterRail) {
        window.requestAnimationFrame(function revealActiveFilter() {
          var targetLeft = activeFilter.offsetLeft - (filterRail.clientWidth - activeFilter.offsetWidth) / 2;
          filterRail.scrollLeft = Math.max(0, targetLeft);
        });
      }
    }

    var searchForm = root.querySelector('.cud-lc__search');
    if (searchForm) {
      searchForm.addEventListener('submit', function validateLearningCenterSearch(event) {
        var input = searchForm.querySelector('input[type="search"]');
        if (!input || input.value.trim()) {
          return;
        }

        event.preventDefault();
        input.setAttribute('aria-invalid', 'true');
        input.focus();

        window.setTimeout(function clearSearchError() {
          input.removeAttribute('aria-invalid');
        }, 1800);
      });
    }
  }

  function collectRoots(scope, selector) {
    var roots = [];

    if (isElement(scope) && scope.matches(selector)) {
      roots.push(scope);
    }

    if (scope && typeof scope.querySelectorAll === 'function') {
      roots = roots.concat(Array.from(scope.querySelectorAll(selector)));
    }

    return roots;
  }

  function init(scope) {
    var target = scope || document;

    collectRoots(target, ARTICLE_SELECTOR).forEach(initArticleRoot);
    collectRoots(target, LEARNING_CENTER_SELECTOR).forEach(initLearningCenter);
  }

  function destroy(scope) {
    var target = scope || document;

    Array.from(controllers).forEach(function destroyMatchingController(controller) {
      if (
        controller.root === target ||
        (target && typeof target.contains === 'function' && target.contains(controller.root))
      ) {
        controller.destroy();
        controllerByRoot.delete(controller.root);
        controllers.delete(controller);
      }
    });
  }

  function refresh(scope) {
    destroy(scope);
    init(scope);
  }

  window.CUDOLLABlog = {
    version: VERSION,
    init: init,
    destroy: destroy,
    refresh: refresh
  };

  function boot() {
    init(document);

    document.addEventListener('shopify:section:load', function onSectionLoad(event) {
      init(event.target);
    });

    document.addEventListener('shopify:section:unload', function onSectionUnload(event) {
      destroy(event.target);
    });

    document.addEventListener('shopify:block:select', function onBlockSelect(event) {
      init(event.target.closest ? event.target.closest('.shopify-section') || event.target : event.target);
    });

    window.addEventListener('pageshow', function onPageShow() {
      init(document);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
})();
